import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, ArrowLeft } from "lucide-react";
import { api, fmtDate } from "../../lib/api";
import ImageField from "../../components/admin/ImageField";
import Select from "../../components/Select";

const AUTHORS = ["TBOI Staff", "TBOI Chief Editor", "Forum Dhanak", "Ami", "Mansi Dhanak"];
const CATEGORIES = ["Editorial", "Haute Couture", "Real Weddings", "Bespoke Decor", "Destination Guides", "Celebrity Spotlights"];

const EMPTY = { title: "", slug: "", excerpt: "", content: "", cover_image: "", category: "Editorial", author: "TBOI Staff", published: true, featured: false };

export default function ArticlesTab() {
  const [blogs, setBlogs] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api("/admin/blogs").then(setBlogs).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) {
        await api(`/admin/blogs/${editing.id}`, { method: "PUT", body: editing });
        toast.success("Article updated");
      } else {
        await api("/admin/blogs", { method: "POST", body: editing });
        toast.success("Article published");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"? This cannot be undone.`)) return;
    try {
      await api(`/admin/blogs/${b.id}`, { method: "DELETE" });
      toast.success("Article deleted");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (editing) {
    const set = (k, v) => setEditing({ ...editing, [k]: v });
    return (
      <div data-testid="article-editor">
        <button onClick={() => setEditing(null)} data-testid="article-back-button" className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-ink/60 hover:text-gold font-semibold">
          <ArrowLeft size={14} /> All Articles
        </button>
        <h2 className="font-display text-3xl text-ink mt-4">{editing.id ? "Edit Article" : "New Article"}</h2>

        <div className="mt-8 space-y-5">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">Title</label>
            <input data-testid="article-title-input" value={editing.title} onChange={(e) => set("title", e.target.value)}
              className="w-full border border-ink/15 px-4 py-3 text-sm outline-none focus:border-gold bg-white" />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">Slug (optional)</label>
              <input data-testid="article-slug-input" value={editing.slug} onChange={(e) => set("slug", e.target.value)}
                placeholder="auto-generated-from-title"
                className="w-full border border-ink/15 px-4 py-3 text-sm outline-none focus:border-gold bg-white" />
            </div>
            <ImageField label="Cover Image" value={editing.cover_image} onChange={(v) => set("cover_image", v)} testid="article-cover-input" />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">Author</label>
              <Select testid="article-author-select" value={editing.author} onChange={(v) => set("author", v)} options={AUTHORS} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">Category</label>
              <Select testid="article-category-select" value={editing.category} onChange={(v) => set("category", v)} options={CATEGORIES} />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">Excerpt</label>
            <textarea data-testid="article-excerpt-input" rows={2} value={editing.excerpt} onChange={(e) => set("excerpt", e.target.value)}
              className="w-full border border-ink/15 px-4 py-3 text-sm outline-none focus:border-gold bg-white" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">Story</label>
            <p className="text-xs text-neutral-500 mb-2">
              Separate paragraphs with a blank line. Start a line with ## for a heading, &gt; for a pull quote, img: URL | caption for a photo, and wrap a line in * for an italic closing note.
            </p>
            <textarea data-testid="article-content-input" rows={14} value={editing.content} onChange={(e) => set("content", e.target.value)}
              className="w-full border border-ink/15 px-4 py-3 text-sm outline-none focus:border-gold bg-white font-mono" />
          </div>
          <div className="flex gap-8">
            <label className="flex items-center gap-3 text-sm text-ink/80">
              <input type="checkbox" data-testid="article-published-toggle" checked={editing.published} onChange={(e) => set("published", e.target.checked)} className="w-4 h-4 accent-[#B89742]" />
              Published
            </label>
            <label className="flex items-center gap-3 text-sm text-ink/80">
              <input type="checkbox" data-testid="article-featured-toggle" checked={editing.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[#B89742]" />
              Featured on home
            </label>
          </div>
          <button onClick={save} data-testid="article-save-button" disabled={!editing.title}
            className="bg-ink text-cream px-8 py-3.5 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-gold transition-colors duration-300 disabled:opacity-40">
            {editing.id ? "Save Changes" : "Publish Article"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="articles-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl text-ink">Articles</h2>
          <p className="mt-2 text-sm text-neutral-500">Create, edit and retire stories from The Edit.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="article-new-button"
          className="flex items-center gap-2 bg-ink text-cream px-6 py-3 text-[10px] uppercase tracking-[0.25em] font-semibold hover:bg-gold transition-colors duration-300">
          <Plus size={14} /> New Article
        </button>
      </div>

      <div className="mt-8 divide-y divide-gold/15 border-y border-gold/15">
        {!blogs ? (
          <p className="py-6 font-display italic text-gold">Loading</p>
        ) : blogs.length === 0 ? (
          <p className="py-6 text-sm text-neutral-500">No articles yet.</p>
        ) : (
          blogs.map((b) => (
            <div key={b.id} data-testid={`article-row-${b.slug}`} className="py-5 flex items-center gap-5">
              {b.cover_image && <img src={b.cover_image} alt="" className="w-16 h-16 object-cover shrink-0 hidden sm:block" />}
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg text-ink truncate">{b.title}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-ink/50 mt-1">
                  {b.category} · {b.author} · {fmtDate(b.created_at)} · {b.published ? "Live" : "Draft"}
                </p>
              </div>
              <button onClick={() => setEditing({ ...EMPTY, ...b })} data-testid={`article-edit-${b.slug}`} aria-label="Edit"
                className="w-10 h-10 border border-ink/15 flex items-center justify-center text-ink/60 hover:border-gold hover:text-gold transition-colors duration-300">
                <Pencil size={15} />
              </button>
              <button onClick={() => remove(b)} data-testid={`article-delete-${b.slug}`} aria-label="Delete"
                className="w-10 h-10 border border-ink/15 flex items-center justify-center text-ink/60 hover:border-red-400 hover:text-red-500 transition-colors duration-300">
                <Trash2 size={15} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
